import * as React from "react";
import { Checkbox } from "../checkbox/Checkbox";
import { RadioGroupItem } from "../radio-group";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { cn } from "../../utils";

export interface InputProps extends Omit<React.ComponentProps<"input">, "ref" | "size"> {
  icon?: React.ComponentType<{ size?: string | number; className?: string }>;
  iconPosition?: "left" | "right";
  /** When true, the focus ring appears inside the input border instead of outside */
  insetRing?: boolean;
  size?: "sm" | "md" | "lg";
}

const sizeStyles: Record<"sm" | "md" | "lg", string> = {
  sm: "al-h-8 al-py-1 al-text-sm",
  md: "al-h-10 al-py-2 al-text-base",
  lg: "al-h-12 al-py-3 al-text-lg",
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      icon: IconComponent,
      iconPosition = "left",
      insetRing = false,
      size = "md",
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "al-flex al-w-full al-rounded-md al-border al-border-input al-bg-background al-ring-offset-background file:al-border-0 file:al-bg-transparent file:al-text-sm file:al-font-medium file:al-text-foreground placeholder:al-text-muted-foreground focus-visible:al-outline-none disabled:al-cursor-not-allowed disabled:al-opacity-50 md:al-text-sm";
    const ringStyles = insetRing
      ? "focus-visible:al-ring-1 focus-visible:al-ring-inset focus-visible:al-ring-ring"
      : "focus-visible:al-ring-2 focus-visible:al-ring-ring focus-visible:al-ring-offset-2";

    if (type === "checkbox") {
      return (
        <Checkbox
          className={className}
          {...(props as React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>)}
        />
      );
    }

    if (type === "radio") {
      return (
        <RadioGroupItem
          className={className}
          {...(props as React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>)}
        />
      );
    }

    if (IconComponent) {
      return (
        <div className="al-relative al-w-full">
          <input
            type={type}
            className={cn(
              baseStyles,
              sizeStyles[size],
              ringStyles,
              iconPosition === "left" ? "al-pl-10 al-pr-3" : "al-pl-3 al-pr-10",
              className
            )}
            ref={ref}
            {...props}
          />
          <div
            className={cn(
              "al-absolute al-top-0 al-flex al-items-center al-pointer-events-none",
              size === "sm" ? "al-h-8" : size === "lg" ? "al-h-12" : "al-h-10",
              iconPosition === "left" ? "al-left-3" : "al-right-3"
            )}
          >
            <IconComponent size={16} className="al-text-muted-foreground" />
          </div>
        </div>
      );
    }

    return (
      <input
        type={type}
        className={cn(baseStyles, sizeStyles[size], ringStyles, "al-px-3", className)}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

export { Input };
